import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "services/api";
import { DealerTemporaryAccess } from "types/temporaryAccess.types";

// Define response types
interface Dealer {
  id: number;
  firstName: string;
  lastName: string;
  dealerName?: string;
  email: string;
  country?: string;
  role: string;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DealersResponse {
  success: boolean;
  response: Dealer[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
  };
}

interface CreateDealerUserRequest {
  firstName: string;
  lastName: string;
  dealerName: string;
  email: string;
  country: string;
  password: string;
  role: string;
  isInvited?: boolean;
}

interface CreateDealerUserResponse {
  success: boolean;
  message?: string;
  user?: Dealer;
}

/**
 * Interface for device link data
 */
interface DeviceLink {
  linkId: number; // Updated to linkId instead of id
  deviceId: number;
  serialNumber: string; // Adjusted to match actual structure
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response model for getDealerInfo endpoint
 */
interface DealerInfoResponse {
  success: boolean;
  dealer: Dealer;
  deviceLinks: DeviceLink[];
  totalLinks: number;
  /** Pending and active temporary access; absent on older API versions. */
  temporaryAccess?: DealerTemporaryAccess[];
  error?: null | string;
}

// Interfaces for new operations responses
interface DeleteDealerResponse {
  success: boolean;
  message?: string;
}

interface EnableDisableDealerResponse {
  success: boolean;
  message?: string;
  dealer?: {
    id: number;
    name: string;
    email: string;
    active: boolean;
  };
}

/**
 * Hook to fetch the list of dealer users with pagination
 */
export const useDealers = (
  page = 1,
  limit = 10,
  sortField?: string,
  sortOrder?: string,
  search?: string,
) => {
  return useQuery({
    queryKey: ["dealers", page, limit, sortField, sortOrder, search],
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async (): Promise<DealersResponse> => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (sortField) {
        params.append("sortField", sortField);
      }

      if (sortOrder) {
        params.append("sortOrder", sortOrder);
      }

      if (search && search.trim() !== "") {
        params.append("search", search);
      }

      try {
        const response = await api.get<DealersResponse>(
          `admin/dealer/all?${params.toString()}`,
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching dealers:", error);
        throw error;
      }
    },
  });
};

/**
 * Hook to create a new dealer user
 */
export const useCreateDealerUser = () => {
  return useMutation({
    mutationFn: async (
      userData: CreateDealerUserRequest,
    ): Promise<CreateDealerUserResponse> => {
      try {
        const response = await api.post<CreateDealerUserResponse>(
          `admin/dealer/register`,
          userData,
        );
        return response.data;
      } catch (error: any) {
        // Properly handle and transform errors
        if (error.response) {
          throw new Error(
            error.response.data?.message || "Failed to create dealer",
          );
        }
        throw new Error("Failed to create dealer");
      }
    },
    onError: (error) => {
      console.error("Error creating dealer user:", error);
    },
  });
};

/**
 * Hook to delete a dealer
 * Endpoint: DELETE admin/dealer/{dealerId}
 * Response codes: 200, 401, 403, 404, 500
 */
export const useDeleteDealer = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteDealerResponse, Error, number>({
    mutationFn: async (dealerId: number) => {
      const response = await api.delete<DeleteDealerResponse>(
        `admin/dealer/${dealerId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
    },
    onError: (error) => {
      console.error("Error deleting dealer:", error);
    },
  });
};

/**
 * Hook to fetch detailed information about a specific dealer
 * @param dealerId - The ID of the dealer to fetch
 */
export const useDealerInfo = (dealerId: string | undefined) => {
  return useQuery({
    queryKey: ["dealerInfo", dealerId],
    enabled: !!dealerId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<DealerInfoResponse> => {
      try {
        const response = await api.get<DealerInfoResponse>(
          // dpp-backend returns `temporaryAccess`; legacy admin service does not.
          `dpp/admin/dealer/${dealerId}/info`,
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching dealer info:", error);
        throw error;
      }
    },
  });
};

/**
 * Hook to remove a device link from a dealer
 */
export const useRemoveDeviceLink = () => {
  return useMutation({
    mutationFn: async ({
      linkId,
    }: {
      linkId: number;
    }): Promise<{ success: boolean }> => {
      const response = await api.delete(`admin/dealer/link/${linkId}`);
      return response.data;
    },
  });
};

/**
 * Hook for admin to create a link between a dealer and a device
 */
export const useAdminCreateDealerDeviceLink = () => {
  return useMutation({
    mutationFn: async ({
      dealerId,
      serialNumber,
    }: {
      dealerId: number;
      serialNumber: string;
    }): Promise<{
      success: boolean;
      message?: string;
      dealerId?: number;
      serialNumber?: string;
      status?: string;
      linkId?: number;
      /** The dealer's pending temporary access requests cancelled by this link. */
      cancelledTemporaryAccessRequestIds?: number[];
    }> => {
      const serialNumberPrefix =
        process.env.REACT_APP_ENV === "production" ? "dwlg-" : "dlgt-";

      const response = await api.post(`admin/dealer/link`, {
        dealerId,
        serialNumber: `${serialNumberPrefix}${serialNumber}`,
      });
      return response.data;
    },
  });
};

/**
 * Hook to enable a dealer
 */
export const useEnableDealer = () => {
  const queryClient = useQueryClient();

  return useMutation<EnableDisableDealerResponse, Error, number>({
    mutationFn: async (dealerId: number) => {
      const response = await api.post<EnableDisableDealerResponse>(
        `admin/dealer/${dealerId}/enable`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
    },
    onError: (error) => {
      console.error("Error enabling dealer:", error);
    },
  });
};

/**
 * Hook to disable a dealer
 */
export const useDisableDealer = () => {
  const queryClient = useQueryClient();

  return useMutation<EnableDisableDealerResponse, Error, number>({
    mutationFn: async (dealerId: number) => {
      const response = await api.post<EnableDisableDealerResponse>(
        `admin/dealer/${dealerId}/disable`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
    },
    onError: (error) => {
      console.error("Error disabling dealer:", error);
    },
  });
};

/**
 * Hook to send a password reset email to a dealer
 */
export const useSendDealerPasswordReset = () => {
  return useMutation({
    mutationFn: async (
      dealerId: number,
    ): Promise<{ success: boolean; message: string }> => {
      const response = await api.post<{ success: boolean; message: string }>(
        `admin/dealer/${dealerId}/send-password-reset`,
      );
      return response.data;
    },
    onError: (error) => {
      console.error("Error sending dealer password reset:", error);
    },
  });
};

/**
 * Hook to update a dealer's password
 */
export const useUpdateDealerPassword = () => {
  return useMutation({
    mutationFn: async ({
      dealerId,
      newPassword,
    }: {
      dealerId: number;
      newPassword: string;
    }): Promise<{ success: boolean; message: string }> => {
      const response = await api.post<{ success: boolean; message: string }>(
        `admin/dealer/${dealerId}/change-password`,
        { newPassword },
      );
      return response.data;
    },
    onError: (error) => {
      console.error("Error updating dealer password:", error);
    },
  });
};

export interface PatchDealerRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dealerName?: string;
  city?: string;
  country?: string;
  language?: string;
}

/**
 * Hook to patch (edit) dealer details
 */
export const usePatchDealer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dealerId,
      data,
    }: {
      dealerId: string;
      data: PatchDealerRequest;
    }): Promise<{ success: boolean; message: string }> => {
      const response = await api.patch<{ success: boolean; message: string }>(
        `admin/dealer/${dealerId}`,
        data,
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["dealerInfo", variables.dealerId],
      });
    },
  });
};

export type {
  Dealer,
  
  
  
  DeviceLink,
  DealerInfoResponse,
};
